import React from 'react';
import { FiAlertCircle, FiLock, FiTrash2, FiArrowLeft } from 'react-icons/fi';

export type ProfileStatus = 'active' | 'deactivated' | 'suspended' | 'deleted';

interface ProfileStatusStateProps {
  status: Exclude<ProfileStatus, 'active'>;
  onBack?: () => void;
}

export const ProfileStatusState: React.FC<ProfileStatusStateProps> = ({ status, onBack }) => {
  const configs = {
    deactivated: {
      title: 'Account Deactivated',
      message: 'This account has been deactivated by the user.',
      icon: FiLock,
      color: '#A1A1AA', // Zinc-400
      accent: 'zinc',
    },
    suspended: {
      title: 'Account Restricted',
      message:
        'This profile is currently unavailable due to a violation of our community guidelines.',
      icon: FiAlertCircle,
      color: '#F87171', // Red-400
      accent: 'red',
    },
    deleted: {
      title: 'Profile Unavailable',
      message: "The profile you're looking for doesn't exist or has been permanently removed.",
      icon: FiTrash2,
      color: '#71717A', // Zinc-500
      accent: 'zinc',
    },
  };

  const config = configs[status];
  const Icon = config.icon;

  return (
    <div className="relative min-h-[60vh] w-full flex flex-col items-center justify-center px-6 text-center">
      {/* Background Glow */}
      <div
        className="absolute pointer-events-none rounded-full opacity-20 blur-[120px]"
        style={{
          width: '400px',
          height: '400px',
          background: `radial-gradient(circle, ${config.color} 0%, transparent 70%)`,
          zIndex: 0,
        }}
      />

      <div className="relative z-10 max-w-md w-full">
        <div className="mb-8 flex justify-center">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center border backdrop-blur-sm shadow-2xl transition-transform duration-500 hover:scale-110"
            style={{
              backgroundColor: `${config.color}10`,
              borderColor: `${config.color}40`,
              boxShadow: `0 0 40px ${config.color}15`,
            }}
          >
            <Icon size={40} style={{ color: config.color }} strokeWidth={1.5} />
          </div>
        </div>

        <h1 className="text-white text-[32px] md:text-[42px] font-bold leading-tight mb-4 tracking-tight font-['Funnel_Display']">
          {config.title}
        </h1>

        <p className="text-zinc-400 text-[16px] md:text-[18px] mb-12 font-medium leading-relaxed">
          {config.message}
        </p>

        {onBack && (
          <button
            onClick={onBack}
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-black rounded-full font-bold text-[15px] hover:bg-zinc-200 transition-all active:scale-[0.98] shadow-xl shadow-white/5"
          >
            <FiArrowLeft size={18} />
            Go Back
          </button>
        )}
      </div>
    </div>
  );
};
